<template>

    <ui-view class="view-cohort-detail" v-spinner="!parcels">

        <ui-heading title="Partij volgen" icon="magicon-jump-right-up"/>

        <ui-panel class="cohort-selection">
            <div class="status">
                <span class="label">Wil je een actieve of passieve partij bekijken?</span>
                <div class="segmented-control">
                    <ui-button
                        type="bordered"
                        color="highlight-1"
                        :class="{ active: cohortMode == 'ACTIVE' }"
                        @click="cohortMode = 'ACTIVE'">Actief</ui-button>
                    <ui-button
                        type="bordered"
                        color="highlight-1"
                        :class="{ active: cohortMode == 'PASSIVE' }"
                        @click="cohortMode = 'PASSIVE'">Passief</ui-button>
                </div>
            </div>
            <div class="cohort">
                <span class="label">Kies een partij</span>
                <div class="dropdowns-holder">
                    <ui-dropdown
                        placeholder="Perceel"
                        searchable
                        :options="parcelOptions"
                        v-model="parcelId"/>
                    <ui-dropdown
                        placeholder="Vak"
                        searchable
                        :disabled="!parcelId"
                        :options="sectionOptions"
                        v-model="sectionId"/>
                    <ui-dropdown
                        placeholder="Cohort"
                        :disabled="!sectionId"
                        :options="cohortOptions"
                        v-model="cohortId"/>
                </div>
            </div>
            <div class="create">
                <ui-button
                    color="positive"
                    icon="magicon-reload-2"
                    :disabled="!parcelId || !sectionId || !cohortId"
                    @click="execute = true"
                    :loading="execute">Creeër overzicht</ui-button>
            </div>
        </ui-panel>

        <div v-if="cohort" v-spinner="execute">

            <ui-heading level="2" :title="cohortTitle" icon="fa-th"/>

            <div class="data-panels">
                <ui-panel class="state-info">
                    <span class="key">Status</span>
                    <span class="value active" v-if="cohort.active">Op perceel</span>
                    <span class="value passive" v-else>Niet op perceel</span>
                </ui-panel>
                <ui-panel class="parcel-info">
                    <span class="key">Perceel</span>
                    <span class="value">{{ cohort.section.parcel.alias || `${cohort.section.parcel.parcelLocation.name} ${cohort.section.parcel.parcelLocation.number}` }}</span>
                </ui-panel>
                <ui-panel class="section-info">
                    <span class="key">Vak</span>
                    <span class="value">{{ cohort.section.name }}</span>
                </ui-panel>
            </div>

            <ui-panel class="history" :padding="false">
                <h2><i class="fa-clock-o"></i>Historie</h2>
                <ui-table class="action-table" clickable>
                    <ui-table-header>
                        <ui-th weight="1.5">Datum</ui-th>
                        <ui-th weight="2">Actie</ui-th>
                        <ui-th weight="1">Bruto</ui-th>
                        <ui-th weight="1">Netto</ui-th>
                        <ui-th weight="1">Gewicht</ui-th>
                        <ui-th weight="1">Tarra</ui-th>
                        <ui-th weight="1">Bustal</ui-th>
                        <ui-th weight="1">Stukstal</ui-th>
                        <ui-th weight="1"/>
                    </ui-table-header>
                    <ui-tr
                        v-for="action in getActions(cohort)"
                        :total="cohort.actions.length"
                        :key="action.__typename + ':' + action.id"
                        click="detail">
                        <td>{{ action.date | date("DD-MM-YYYY") }}</td>
                        <td>{{ action.__typename | actionTypeTitle(action) }}</td>
                        <td><template v-if="action.brutoWeight">{{ action.brutoWeight | number }} mTon</template><template v-else>-</template></td>
                        <td><template v-if="action.nettoWeight">{{ action.nettoWeight | number }} mTon</template><template v-else>-</template></td>
                        <td><template v-if="action.brutoWeight">{{ action.brutoWeight * 100 | number }} kg</template><template v-else>-</template></td>
                        <td><template v-if="action.tarra">{{ action.tarra | number }}%</template><template v-else>-</template></td>
                        <td>{{ action.bustal | number | empty }}</td>
                        <td>{{ action.stukstal | number | empty }}</td>
                        <td class="actions" @click.stop>
                            <ui-link
                                icon="entypo-eye"
                                v-tooltip="'Details'"
                                action="detail"
                                @click="openActionDetailModal(action, cohort)" />
                        </td>
                    </ui-tr>
                </ui-table>
            </ui-panel>
        </div>

    </ui-view>

</template>

<script>

import Vue from 'vue';
import moment from 'moment';
import Component from 'vue-class-component';
import { filter, find, map } from 'es-toolkit/compat';

import createQuery from 'js-core/js/apollo/queries/createQuery';

import ActionType from 'common/enums/ActionType';

import AllParcelsQuery from 'gql/queries/CohortDetail/AllParcels.gql';
import FindCohortQuery from 'gql/queries/CohortDetail/FindCohort.gql';

import ActionDetailModal from 'app/components/modals/ActionDetailModal.vue';

@Component({

    name: 'CohortDetail',
    apollo: {
        parcels: createQuery(AllParcelsQuery),
        cohort: createQuery(FindCohortQuery, {

            skip() {

                return !this.execute || !this.cohortId;
            },

            variables() {

                return {
                    id: this.cohortId
                };
            },

            response() {

                this.execute = false;
                this.created = true;
            }
        })
    }
})
export default class CohortDetail extends Vue
{
    cohortMode = 'ACTIVE';

    created = false;
    execute = false;

    parcels = null;
    cohort = null;

    parcelId = null;
    sectionId = null;
    cohortId = null;

    breadcrumb(){

        return 'Partij volgen';
    }

    mounted() {

        this.$watch('parcelId', () => {

            this.sectionId = null;
        });

        this.$watch('sectionId', () => {

            this.cohortId = null;
        });

        this.$watch('cohortMode', () => {

            this.cohortId = null;
        });

        this.$watch('cohortId', () => {

            this.created = false;
        });
    }

    getActions(cohort){

        return cohort.actions.filter(action => action.__typename !== ActionType.TREATMENT);
    }

    async openActionDetailModal(action, cohort) {

        let treatment = null;

        if(action.__typename === ActionType.SOWING){
            treatment = cohort.actions.find(actionItem => actionItem.__typename === ActionType.TREATMENT && actionItem.sowingId === action.id) || null;
        }
        else if(action.__typename === ActionType.HARVEST){
            treatment = cohort.actions.find(actionItem => actionItem.__typename === ActionType.TREATMENT && actionItem.harvestId === action.id) || null;
        }

        this.$modal(ActionDetailModal, {
            action,
            section: cohort.section,
            treatment,
            parcel: this.selectedParcel
        });
    }

    get parcelOptions() {

        if(this.parcels == null) {
            return [];
        }

        return map(this.parcels.items, parcel => {

            var label = parcel.alias || `${parcel.parcelLocation.name} ${parcel.parcelLocation.number}`;

            return {
                label,
                value: parcel.id
            };
        });
    }

    get selectedParcel() {

        if (!this.parcelId) {
            return null;
        }

        return find(this.parcels.items, { id: this.parcelId });
    }

    get sectionOptions() {

        let parcel = this.selectedParcel;

        if(!this.parcels || !parcel) {
            return [];
        }

        const options = map(parcel.sections.items, section => ({
            label: section.name,
            value: section.id
        }));

        if(options.length === 1) {
            Vue.nextTick(() =>{
                this.sectionId = options[0].value;
            });
        }

        return options;
    }

    get selectedSection() {

        if (!this.sectionId) {
            return null;
        }

        return find(this.selectedParcel.sections.items, { id: this.sectionId });
    }

    get cohortOptions() {

        let section = this.selectedSection;

        if(!this.parcels || !section) {
            return [];
        }

        const filteredCohorts = filter(section.cohorts.items, cohort => this.cohortMode == 'ACTIVE' ? cohort.active : !cohort.active);

        const options = map(filteredCohorts, cohort => ({
            label: cohort.startDate ? moment(cohort.startDate).format('LL') : '-',
            value: cohort.id
        }));

        if(options.length === 1) {
            Vue.nextTick(() =>{
                this.cohortId = options[0].value;
            });
        }

        return options;
    }

    get cohortTitle() {

        var date = this.cohort.startDate ? moment(this.cohort.startDate).format('LL') : '-';

        return `Partij: ${date}`;
    }
}

</script>